import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { BackButton } from "../components/app/BackButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Bell, BellOff, X, Search, BarChart3, Loader2, ArrowRight, TrendingUp } from "lucide-react";

const MarketBadge = ({ type }) => {
  const map = { CRYPTO: "border-bull/40 text-bull", FOREX: "border-warn/40 text-warn", EQUITY: "border-white/15 text-neutral-300" };
  return <span className={`font-mono text-[10px] tracking-[0.18em] px-2 py-0.5 border ${map[type] || map.EQUITY}`}>{type}</span>;
};

const DirectionDot = ({ d }) => {
  const c = d === "BULLISH" ? "bg-bull" : d === "BEARISH" ? "bg-bear" : "bg-neutral-500";
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${c}`} />;
};

const AddSymbolDialog = ({ open, onOpenChange, onAdd }) => {
  const { t } = useI18n();
  const [symbol, setSymbol] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [marketType, setMarketType] = useState("CRYPTO");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!symbol.trim()) return;
    setBusy(true);
    try {
      const ok = await onAdd({ symbol: symbol.toUpperCase().trim(), display_name: displayName.trim() || symbol.toUpperCase().trim(), market_type: marketType });
      if (ok) {
        setSymbol(""); setDisplayName(""); setMarketType("CRYPTO");
        onOpenChange(false);
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-none max-w-md" data-testid="add-symbol-dialog">
        <DialogTitle className="text-xl font-semibold tracking-tight">{t("watchlist_add")}</DialogTitle>
        <DialogDescription className="text-neutral-400 text-sm">
          Track a stock, crypto pair, or forex pair.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-3 mt-4">
          <div>
            <label className="label-mono">Symbol</label>
            <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="BTCUSD · EURUSD · AAPL"
              className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono uppercase tracking-wider focus-visible:ring-bull/40 focus-visible:border-bull/40"
              data-testid="add-symbol-input" required />
          </div>
          <div>
            <label className="label-mono">Display name (optional)</label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Bitcoin / USD"
              className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 focus-visible:ring-bull/40 focus-visible:border-bull/40"
              data-testid="add-symbol-name-input" />
          </div>
          <div>
            <label className="label-mono">Market</label>
            <Select value={marketType} onValueChange={setMarketType}>
              <SelectTrigger className="mt-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono" data-testid="add-symbol-market-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                <SelectItem value="CRYPTO">CRYPTO</SelectItem>
                <SelectItem value="FOREX">FOREX</SelectItem>
                <SelectItem value="EQUITY">EQUITY</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={busy} className="flex-1 rounded-none bg-bull text-black hover:bg-bull/90 h-10 font-medium" data-testid="add-symbol-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("watchlist_add")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AlertDialog = ({ open, onOpenChange, item, onSave }) => {
  const { t } = useI18n();
  const [price, setPrice] = useState(item?.alert?.price || "");
  const [direction, setDirection] = useState(item?.alert?.direction || "ABOVE");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setPrice(item.alert?.price ?? "");
      setDirection(item.alert?.direction || "ABOVE");
    }
  }, [item]);

  const submit = async (e) => {
    e.preventDefault();
    const p = parseFloat(price);
    if (!isFinite(p) || p <= 0) { toast.error("Invalid price"); return; }
    setBusy(true);
    try { await onSave(item.symbol, { price: p, direction }); onOpenChange(false); } finally { setBusy(false); }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-none max-w-md" data-testid="alert-dialog">
        <DialogTitle className="text-xl">{t("watchlist_alert_modal_title")} — <span className="font-mono text-bull">{item.symbol}</span></DialogTitle>
        <form onSubmit={submit} className="space-y-3 mt-4">
          <div>
            <label className="label-mono">{t("watchlist_alert_me")}</label>
            <div className="mt-1 flex gap-2">
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className="bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono w-32" data-testid="alert-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 rounded-none">
                  <SelectItem value="ABOVE">{t("watchlist_alert_above")}</SelectItem>
                  <SelectItem value="BELOW">{t("watchlist_alert_below")}</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" step="any" required value={price} onChange={(e) => setPrice(e.target.value)} placeholder="70000"
                className="flex-1 bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                data-testid="alert-price-input" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={busy} className="flex-1 rounded-none bg-bull text-black hover:bg-bull/90 h-10 font-medium" data-testid="alert-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("watchlist_alert_set")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Watchlist() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertItem, setAlertItem] = useState(null);
  const [historySym, setHistorySym] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/login", { replace: true }); }, [user, loading, navigate]);

  const load = async () => {
    try { const { data } = await api.get("/watchlist"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const addSymbol = async (payload) => {
    try {
      await api.post("/watchlist", payload);
      toast.success(`${payload.symbol} added`);
      load();
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "WATCHLIST_LIMIT") toast.error(t("watchlist_limit"));
      else toast.error(typeof detail === "string" ? detail : "Failed to add");
      return false;
    }
  };

  const remove = async (sym) => {
    try { await api.delete(`/watchlist/${sym}`); setItems((i) => i.filter((x) => x.symbol !== sym)); }
    catch { toast.error("Failed to remove"); }
  };

  const setAlert = async (sym, cfg) => {
    await api.put(`/watchlist/${sym}/alert`, cfg);
    toast.success("Alert set");
    load();
  };

  const removeAlert = async (sym) => {
    try { await api.delete(`/watchlist/${sym}/alert`); load(); } catch { /* ignore */ }
  };

  const openHistory = async (sym) => {
    setHistorySym(sym); setHistoryLoading(true); setHistory([]);
    try { const { data } = await api.get(`/watchlist/${sym}/analyses`); setHistory(data); }
    catch { /* ignore */ } finally { setHistoryLoading(false); }
  };

  const filtered = items.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return i.symbol.toLowerCase().includes(q) || (i.display_name || "").toLowerCase().includes(q);
  });

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-bull" /></div>;

  return (
    <div className="min-h-screen" data-testid="watchlist-page">
      <Header />
      <main className="mx-auto max-w-5xl px-6 md:px-8 py-8 space-y-6">
        <BackButton fallback="/dashboard" />

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="label-mono">DASHBOARD //</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter">{t("watchlist_title")}<span className="text-bull">_</span></h1>
          </div>
          <Button onClick={() => setAddOpen(true)} className="rounded-none bg-bull text-black hover:bg-bull/90 h-10 font-medium" data-testid="watchlist-add-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} /> {t("watchlist_add")}
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search symbol..."
            className="pl-10 bg-[#0e0e0e] border-white/10 rounded-none h-11 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
            data-testid="watchlist-search-input" />
        </div>

        {filtered.length === 0 ? (
          <div className="surface-card p-12 text-center" data-testid="watchlist-empty">
            <TrendingUp className="h-7 w-7 mx-auto text-neutral-600" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-neutral-500">{t("watchlist_empty")}</p>
          </div>
        ) : (
          <div className="surface-card divide-y divide-white/5" data-testid="watchlist-list">
            {filtered.map((it) => (
              <div key={it.watch_id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 group" data-testid={`watchlist-item-${it.symbol}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-lg font-medium text-white">{it.symbol}</span>
                    <span className="text-sm text-neutral-400 truncate">{it.display_name}</span>
                    <MarketBadge type={it.market_type} />
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                    {it.analyses_count > 0 && (
                      <button onClick={() => openHistory(it.symbol)} className="font-mono text-[11px] text-neutral-500 hover:text-bull inline-flex items-center gap-1" data-testid={`history-btn-${it.symbol}`}>
                        <BarChart3 className="h-3 w-3" strokeWidth={1.5} /> {it.analyses_count} {t("watchlist_analyses_count")}
                      </button>
                    )}
                    {it.alert?.enabled && (
                      <span className="font-mono text-[11px] inline-flex items-center gap-1 text-warn" data-testid={`alert-active-${it.symbol}`}>
                        <Bell className="h-3 w-3" strokeWidth={1.5} /> {it.alert.direction === "ABOVE" ? "↑" : "↓"} {it.alert.price}
                        {it.alert.triggered && <span className="ml-1 text-bull">✓ fired</span>}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button size="sm" variant="outline"
                    className="rounded-none border-white/15 bg-transparent hover:bg-bull/5 hover:border-bull/40 hover:text-bull h-8"
                    onClick={() => navigate(`/dashboard?symbol=${encodeURIComponent(it.symbol)}&market_type=${it.market_type}`)}
                    data-testid={`analyze-btn-${it.symbol}`}>
                    {t("watchlist_analyze")}
                  </Button>
                  <Button size="sm" variant="outline"
                    className={`rounded-none border-white/15 bg-transparent hover:bg-white/5 h-8 ${it.alert?.enabled ? "text-warn border-warn/30" : "text-neutral-400"}`}
                    onClick={() => { setAlertItem(it); setAlertOpen(true); }}
                    data-testid={`alert-btn-${it.symbol}`}>
                    <Bell className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  {it.alert?.enabled && (
                    <Button size="sm" variant="ghost"
                      className="rounded-none text-neutral-500 hover:text-bear hover:bg-bear/5 h-8 px-2"
                      onClick={() => removeAlert(it.symbol)}
                      data-testid={`alert-remove-${it.symbol}`}>
                      <BellOff className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost"
                    className="rounded-none text-neutral-500 hover:text-bear hover:bg-bear/5 h-8 px-2"
                    onClick={() => remove(it.symbol)}
                    data-testid={`remove-btn-${it.symbol}`}>
                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OWNER/PRO/TRADER unlimited */}
        {user.role !== "OWNER" && user.plan === "FREE" && (
          <p className="font-mono text-[11px] text-neutral-500 text-center">
            {items.length} / 20 symbols · <button onClick={() => navigate("/pricing")} className="text-bull hover:underline">Upgrade for unlimited</button>
          </p>
        )}
      </main>

      <AddSymbolDialog open={addOpen} onOpenChange={setAddOpen} onAdd={addSymbol} />
      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen} item={alertItem} onSave={setAlert} />

      {/* History side sheet */}
      <Sheet open={!!historySym} onOpenChange={(o) => { if (!o) setHistorySym(null); }}>
        <SheetContent className="bg-[#0a0a0a] border-white/10 w-full sm:max-w-md overflow-y-auto" data-testid="history-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono tracking-wider">{historySym} · ANALYSES</SheetTitle>
          </SheetHeader>
          {historyLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-bull" /></div>
          ) : history.length === 0 ? (
            <p className="mt-6 text-sm text-neutral-500 font-mono">No analyses yet.</p>
          ) : (
            <div className="mt-6 space-y-3">
              {history.map((a) => (
                <button key={a.analysis_id} onClick={() => navigate(`/analysis/${a.analysis_id}`)} className="w-full text-left surface-card p-4 hover:border-white/20 transition-colors" data-testid={`history-row-${a.analysis_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 font-mono text-sm">
                      <DirectionDot d={a.direction} />
                      <span className={a.direction === "BULLISH" ? "text-bull" : a.direction === "BEARISH" ? "text-bear" : "text-neutral-300"}>{a.direction}</span>
                      <span className="text-neutral-500">{a.confidence}%</span>
                    </span>
                    <span className="font-mono text-[10px] text-neutral-500">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                  {a.timeframe && <div className="mt-1 font-mono text-[11px] text-neutral-400">{a.timeframe}</div>}
                  <div className="mt-2 flex items-center justify-end text-bull text-xs font-mono">View <ArrowRight className="h-3 w-3 ml-1" /></div>
                </button>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
